import {
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  useTheme,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Views } from "react-big-calendar";
import dayjs from "dayjs";
import { useCallback, useState } from "react";
import { getUTCTimeZoneOffset } from "../../utils/utils";
import { useLocation } from "react-router-dom";
import SummaryPage from "./SummaryPage";

const VIEW_OPTIONS = [
  { id: Views.DAY, label: "Day" },
  { id: Views.WEEK, label: "Week" },
  { id: Views.MONTH, label: "Month" },
  { id: "custom", label: "Custom" },
];
type ViewType = (typeof Views)[keyof typeof Views] | "custom";

// type Keys = keyof typeof Views;

const AdminPage = () => {
  const location = useLocation();
  const theme = useTheme();
  const initialDate = dayjs().toDate();
  const [date, setDate] = useState<Date>(initialDate);
  const [view, setView] = useState<ViewType>(Views.DAY);
  const [customRange, setCustomRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null]
  >([
    dayjs(location.state?.startEndTime?.start_time) || null,
    dayjs(location.state?.startEndTime?.end_time) || null,
  ]);

  const handleDateChange = (value: any) => {
    if (value && dayjs(value).isValid()) {
      const updatedDate = value.toDate();
      setDate(updatedDate);
    } else {
      alert("Please select a valid date.");
    }
  };

  const handleCustomRangeChange = ([start, end]: [
    dayjs.Dayjs | null,
    dayjs.Dayjs | null,
  ]) => {
    setCustomRange([start, end]);
  };

  const onPrevClick = useCallback(() => {
    let newDate: Date = new Date();
    if (view === Views.DAY) {
      newDate = dayjs(date).subtract(1, "day").toDate();
    } else if (view === Views.WEEK) {
      newDate = dayjs(date).subtract(1, "week").toDate();
    } else {
      newDate = dayjs(date).subtract(1, "month").toDate();
    }

    setDate(newDate);
  }, [date, view]);

  const onNextClick = useCallback(() => {
    let newDate: Date = new Date();
    if (view === Views.DAY) {
      newDate = dayjs(date).add(1, "day").toDate();
    } else if (view === Views.WEEK) {
      newDate = dayjs(date).add(1, "week").toDate();
    } else {
      newDate = dayjs(date).add(1, "month").toDate();
    }
    setDate(newDate);
  }, [date, view]);

  const handleChange = (event: any) => {
    const selectedView = event.target.value as ViewType;
    if (selectedView === "custom") setCustomRange([null, null]);
    setView(selectedView);
  };

  const getStartEndMeetingTime = () => {
    const utcTimeOffset = getUTCTimeZoneOffset();
    let startDate = dayjs();
    let endDate = dayjs();

    if (view === Views.DAY) {
      startDate = dayjs(date).startOf("day");
      endDate = dayjs(date).endOf("day");
    } else if (view === Views.WEEK) {
      startDate = dayjs(date).startOf("week");
      endDate = dayjs(date).endOf("week");
    } else if (view === Views.MONTH) {
      startDate = dayjs(date).startOf("month");
      endDate = dayjs(date).endOf("month");
    } else if (view === "custom" && customRange[0] && customRange[1]) {
      startDate = customRange[0];
      endDate = customRange[1];
    }

    return {
      start_time: `${startDate.format("YYYY-MM-DD")}T00:00:00${utcTimeOffset}`,
      end_time: `${endDate.format("YYYY-MM-DD")}T23:59:59${utcTimeOffset}`,
    };
  };

  const formatDateRange = () => {
    const { start_time, end_time } = getStartEndMeetingTime();
    const startDate = dayjs(start_time);
    const endDate = dayjs(end_time);

    if (view === Views.WEEK || view === Views.MONTH) {
      return `${startDate.format("MMM DD, YYYY")} - ${endDate.format(
        "MMM DD, YYYY"
      )}`;
    } else if (view === Views.DAY) {
      return `${startDate.format("MMM DD, YYYY")} - ${endDate.format(
        "MMM DD, YYYY"
      )}`;
    } else if (view === "custom") {
      return customRange[0] && customRange[1]
        ? `${dayjs(customRange[0]).format("MMM DD, YYYY")} - ${dayjs(
            customRange[1]
          ).format("MMM DD, YYYY")}`
        : "Select Date Range";
    }
    return "";
  };

  return (
    <Box
      id="meetingsParent"
      className="flex flex-col justify-start items-start gap-6"
      style={{ marginTop: "-10px" }}
    >
      <Box className="flex flex-row justify-between items-center w-full">
        <Box className="flex flex-col justify-start items-start gap-1">
          <Typography variant="h2">Meeting Summary</Typography>
        </Box>
        <Box className="flex flex-row justify-end items-center gap-2">
          {view !== "custom" && (
            <>
              <IconButton
                size="small"
                onClick={onPrevClick}
                sx={{ marginRight: "-20px" }}
              >
                <NavigateBeforeIcon
                  sx={{
                    fontSize: theme.typography.h2.fontSize,
                    color: "black",
                  }}
                />
              </IconButton>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={dayjs(date)}
                  onChange={handleDateChange}
                  format="DD MMM YYYY"
                  slotProps={{
                    textField: {
                      sx: {
                        marginLeft: "8px",
                        [theme.breakpoints.down("lg")]: {},
                        "& .MuiInputBase-root": {
                          minWidth: "295px",
                          maxWidth: "295px",
                          height: "38px",
                          fontSize: "16px",
                          fontWeight: 600,
                        },
                      },
                      InputProps: {
                        value: formatDateRange(),
                      },
                    },
                  }}
                />
              </LocalizationProvider>

              <IconButton
                size="small"
                onClick={onNextClick}
                sx={{ marginLeft: "-12px" }}
              >
                <NavigateNextIcon
                  sx={{
                    fontSize: theme.typography.h2.fontSize,
                    color: "black",
                  }}
                />
              </IconButton>
            </>
          )}
          {view === "custom" && (
            <>
              <Box
                sx={{
                  border: "1px solid #D9D9D9",
                  borderRadius: "4px",
                  padding: "2px",
                  gap: "4px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={customRange[0]}
                    onChange={(newStart) =>
                      handleCustomRangeChange([newStart, customRange[1]])
                    }
                    format="MMM DD, YYYY"
                    minDate={null}
                    maxDate={customRange[1]}
                    slotProps={{
                      textField: {
                        sx: {
                          marginLeft: "2px",
                          marginRight: "2px",
                          [theme.breakpoints.down("lg")]: {},
                          "& .MuiInputBase-root": {
                            minWidth: "180px",
                            maxWidth: "180px",
                            height: "38px",
                            fontSize: "16px",
                            fontWeight: 600,
                          },
                        },
                        InputProps: {
                          placeholder: "Start Date",
                          readOnly: true,
                        },
                        inputProps: {
                          readOnly: true, 
                        },
                      },
                    }}
                  />
                  <Typography
                    sx={{ marginX: "8px", fontSize: "16px", fontWeight: 600 }}
                  >
                    -
                  </Typography>
                  <DatePicker
                    value={customRange[1]}
                    onChange={(newEnd) =>
                      handleCustomRangeChange([customRange[0], newEnd])
                    }
                    format="MMM DD, YYYY"
                    minDate={customRange[0]}
                    maxDate={null}
                    slotProps={{
                      textField: {
                        sx: {
                          [theme.breakpoints.down("lg")]: {},
                          "& .MuiInputBase-root": {
                            minWidth: "180px",
                            maxWidth: "180px",
                            height: "38px",
                            fontSize: "16px",
                            fontWeight: 600,
                          },
                        },
                        InputProps: {
                          placeholder: "End Date",
                        },
                        inputProps: {
                          readOnly: true,
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </>
          )}

          <Select
            value={view}
            size="small"
            sx={{
              fontWeight: 600,
              height: "38px",
              borderRadius: "4px",
              fontSize: "16px",
              ...(view === "custom"
                ? { minHeight: "43px" }
                : { maxHeight: "37px" }),
            }}
            IconComponent={(props) => {
              const isExpanded = props.className.includes("MuiSelect-iconOpen");
              return isExpanded ? (
                <ExpandLessIcon sx={{ fontSize: "30px", color: "#000000" }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: "30px", color: "#000000" }} />
              );
            }}
            onChange={handleChange}
          >
            {VIEW_OPTIONS.map(({ id, label }) => (
              <MenuItem key={id} value={id}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
      <Box className="flex-grow w-full">
        <SummaryPage startEndMeetingTime={getStartEndMeetingTime()} />
      </Box>
    </Box>
  );
};

export default AdminPage;
